import { useState } from "react";

import { TooltipBase } from "@frontend/common/src/components";

import { RegulatoryListData } from "@/components/business/RegulatoryContainer/Regulatory.types";
import { ProcessStatusCode } from "@/types/code.types";
import { regulatoryListApis } from "@api/regulatory/regulatoryList/regulatory.service";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { useGetCurrentEntryData } from "@hooks/useGetCurrentEntryData";
import useModal from "@hooks/useModal";
import { toastFail } from "@utils/toast";

import * as S from "./RegulatoryCompanyShell.style";
import RegulatoryCheckStatusGuide from "../RegulatoryTitleContainer/components/RegulatoryTableShellForRegulatoryTitle/components/RegulatoryCheckStatusGuide/RegulatoryCheckStatusGuide";
import { useMoveRegulatoryDetailPage } from "../RegulatoryTitleContainer/components/RegulatoryTableShellForRegulatoryTitle/hooks/useMoveRegulatoryDetailPage";

type Props = {
  companyName: string[] | null;
  regulatoryData: RegulatoryListData;
  updateRegulatoryList: () => void;
};

const RegulatoryCompanyShell = ({
  companyName,
  regulatoryData,
  updateRegulatoryList,
}: Props) => {
  const [currentStatus, setCurrentStatus] = useState<ProcessStatusCode | "">(
    "",
  );
  const draftArticleId = regulatoryData?.draftArticleId?.toString();
  const getEntryData = useGetCurrentEntryData(draftArticleId);

  const {
    isModal: isCheckStatusModal,
    closeModal: closeCheckStatusModal,
    openModal: openCheckStatusModal,
  } = useModal();

  const { moveRegulatoryDetailPage } = useMoveRegulatoryDetailPage({
    draftArticleId,
    labelId: regulatoryData?.labelId,
  });

  const saveScrollPositionToSessionStorage = () => {
    sessionStorage.setItem("scrollPosition", window.scrollY.toString());
  };

  const checkCurrentStatus = async () => {
    try {
      const { data: regulatoryCurrentStatus } =
        await regulatoryListApis.checkPressStatus(draftArticleId);

      if (
        regulatoryCurrentStatus === DRAFT_STATUS_CODE.aiEntryDone &&
        regulatoryData.draftArticleStatusHighest ===
          DRAFT_STATUS_CODE.notForAiEntry
      ) {
        saveScrollPositionToSessionStorage();
        moveRegulatoryDetailPage();

        return;
      }

      if (
        regulatoryCurrentStatus !== regulatoryData.draftArticleStatusHighest
      ) {
        setCurrentStatus(regulatoryCurrentStatus as ProcessStatusCode | "");
        openCheckStatusModal();
      } else {
        saveScrollPositionToSessionStorage();
        moveRegulatoryDetailPage();
      }
    } catch (error) {
      toastFail("Could not check the current status of the article.");
    }
  };

  const clickToMoveRegulatoryDetailPage = async () => {
    getEntryData();
    await checkCurrentStatus();
  };

  if (
    !companyName ||
    companyName.length === 0 ||
    companyName.every((item) => !item)
  ) {
    return <S.RegulatoryCompanyShell>-</S.RegulatoryCompanyShell>;
  }

  const visibleCompanies = companyName ? companyName.slice(0, 2) : [];
  const remainingCount =
    companyName && companyName.length > 2 ? companyName.length - 2 : 0;

  return (
    <>
      <S.RegulatoryCompanyShell onClick={clickToMoveRegulatoryDetailPage}>
        {visibleCompanies.map((item) => (
          <S.RegulatoryCompanyItem key={item}>
            <S.RegulatoryCompanyItemText>{item}</S.RegulatoryCompanyItemText>
          </S.RegulatoryCompanyItem>
        ))}

        {remainingCount > 0 && (
          <>
            <S.RegulatoryCompanyNumber>
              +{remainingCount}
            </S.RegulatoryCompanyNumber>
            <S.TooltipWrapper className="tooltip-wrapper">
              <TooltipBase className="tooltip">
                {companyName?.map((item) => (
                  <S.RegulatoryCompanyItem key={item}>
                    <S.RegulatoryCompanyItemText>
                      {item}
                    </S.RegulatoryCompanyItemText>
                  </S.RegulatoryCompanyItem>
                ))}
              </TooltipBase>
            </S.TooltipWrapper>
          </>
        )}
      </S.RegulatoryCompanyShell>

      {isCheckStatusModal && (
        <RegulatoryCheckStatusGuide
          closeModal={closeCheckStatusModal}
          regulatoryData={regulatoryData}
          updateRegulatoryList={updateRegulatoryList}
          currentStatus={currentStatus}
        />
      )}
    </>
  );
};

export default RegulatoryCompanyShell;
